import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Avatar,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  BadgeOutlined as BadgeOutlinedIcon,
  Cancel as CancelIcon,
  ManageAccountsOutlined as ManageAccountsOutlinedIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  TableChart as TableChartIcon,
  MoreHoriz as MoreHorizIcon,
  MailOutline as MailOutlineIcon,
  Block as BlockIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import PaginationControls from "../../components/ui/PaginationControls";
import { useTablePagination } from "../../components/ui/useTablePagination";
import {
  DEFAULT_ROWS_PER_PAGE,
  ROW_HEIGHT,
  USER_COLUMNS,
} from "../constants/userConstants";
import { ReportGenUser } from "../interfaces/User";
import {
  canChangeStationRole,
  getStationRoleLabel,
} from "../constants/roleConstants";

export interface UsersTableProps {
  users: ReportGenUser[];
  isLoading: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  onResendInvite: (user: ReportGenUser) => Promise<void>;
  onResetPassword: (user: ReportGenUser) => Promise<void>;
  onEditBadge: (user: ReportGenUser) => void;
  onEditRole: (user: ReportGenUser) => void;
  onStatusChange: (
    user: ReportGenUser,
    action: "activate" | "deactivate"
  ) => void;
  resendingEmail: string | null;
  resettingPasswordEmail?: string | null;
  statusUpdatingEmail?: string | null;
  /** Caller identity for role-editing gates (self and rank checks). */
  currentUserEmail?: string | null;
  callerRoleSlug?: string | null;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  isLoading,
  searchTerm,
  onSearchChange,
  onRefresh,
  onResendInvite,
  onResetPassword,
  onEditBadge,
  onEditRole,
  onStatusChange,
  resendingEmail,
  resettingPasswordEmail,
  statusUpdatingEmail,
  currentUserEmail,
  callerRoleSlug,
}) => {
  const theme = useTheme();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMenuUser, setActiveMenuUser] = useState<ReportGenUser | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: ReportGenUser) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setActiveMenuUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveMenuUser(null);
  };

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return users;

    return users.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(term) ||
        user.lastName?.toLowerCase().includes(term) ||
        user.rank?.toLowerCase().includes(term) ||
        user.badgeNumber?.toLowerCase().includes(term) ||
        user.department?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        // Match the Role column as displayed (label), not just the slug.
        getStationRoleLabel(user.role)?.toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  const { page, rowsPerPage, totalPages, setPage, setRowsPerPage, start, end } =
    useTablePagination(filteredUsers.length, {
      initialRowsPerPage: DEFAULT_ROWS_PER_PAGE,
    });

  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, users]);

  const renderWithTooltip = (
    value?: string | number | null,
    placeholder = "None"
  ) => {
    const isMissing =
      value === undefined || value === null || String(value).trim() === "";
    const text = isMissing ? placeholder : String(value);

    return (
      <Tooltip
        title={
          <span style={{ fontStyle: isMissing ? "italic" : "normal" }}>
            {text}
          </span>
        }
        arrow
        enterDelay={400}
        slotProps={{
          tooltip: {
            sx: {
              fontSize: ".9rem",
              p: 1.5,
              maxWidth: 400,
            },
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontStyle: isMissing ? "italic" : "normal",
            color: isMissing ? "text.secondary" : "inherit",
          }}
        >
          {text}
        </Box>
      </Tooltip>
    );
  };

  const paginatedUsers = filteredUsers.slice(start, end);

  const handleClearSearch = () => {
    onSearchChange("");
  };

  const statusConfig = (status: string) => {
    switch ((status || "").toUpperCase()) {
      case "ACTIVE":
        return {
          color: theme.palette.success.dark,
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          borderColor: alpha(theme.palette.success.main, 0.3),
          icon: <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} />,
        };
      case "PENDING":
        return {
          color: theme.palette.warning.dark,
          backgroundColor: alpha(theme.palette.warning.main, 0.1),
          borderColor: alpha(theme.palette.warning.main, 0.3),
          icon: <PendingIcon sx={{ fontSize: 14, mr: 0.5 }} />,
        };
      case "INVITED":
        return {
          color: theme.palette.info.dark,
          backgroundColor: alpha(theme.palette.info.main, 0.1),
          borderColor: alpha(theme.palette.info.main, 0.3),
          icon: <MailOutlineIcon sx={{ fontSize: 14, mr: 0.5 }} />,
        };
      default:
        return {
          color: theme.palette.error.dark,
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          borderColor: alpha(theme.palette.error.main, 0.3),
          icon: <CancelIcon sx={{ fontSize: 14, mr: 0.5 }} />,
        };
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        p: 2.5,
        bgcolor: alpha(theme.palette.background.paper, 0.8),
        borderRadius: 2,
        border: `1px solid ${theme.palette.grey[400]}`,
        mb: 1,
      }}
    >
      <Grid
        container
        alignItems="center"
        spacing={1.5}
        sx={{
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Grid
          size={{ xs: 12, md: "auto" }}
          sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
            }}
          >
            <TableChartIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              All Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? `${filteredUsers.length} of ${users.length} users`
                : `${filteredUsers.length} users total`}
            </Typography>
          </Box>
        </Grid>
        <Grid
          size={{ md: "grow" }}
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "flex-end" },
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Search by name, email, badge..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    size="small"
                    edge="end"
                    aria-label="clear search"
                    sx={{
                      p: 0.5,
                      color: "action.active",
                      "&:hover": {
                        color: "text.primary",
                      },
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                alignItems: "center",
              },
            }}
            sx={{
              width: { xs: "100%", sm: 280 },
              "& .MuiOutlinedInput-root": {
                bgcolor: "background.paper",
                borderRadius: 2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                },
                "&.Mui-focused": {
                  bgcolor: "background.paper",
                  boxShadow: `0 0 0 3px ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )}`,
                },
              },
            }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={onRefresh}
            disabled={isLoading}
            startIcon={
              <RefreshIcon
                sx={{
                  transition: "transform 0.3s ease",
                  ...(isLoading && {
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }),
                }}
              />
            }
            sx={{
              height: 40,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              px: 2,
            }}
          >
            Refresh Users
          </Button>
        </Grid>
      </Grid>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          minWidth: 0,
          width: "100%",
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          mt: 1.5,
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            minWidth: USER_COLUMNS.reduce((w, c) => w + c.width, 0),
            "& .MuiTableCell-root": {
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              height: ROW_HEIGHT,
              py: 0,
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
            },
          }}
        >
          <colgroup>
            {USER_COLUMNS.map((col) => (
              <col key={col.key} style={{ width: col.width }} />
            ))}
          </colgroup>

          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.grey[100], 0.8),
                "& .MuiTableCell-head": {
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  py: 1.5,
                },
              }}
            >
              {USER_COLUMNS.map((col) => (
                <TableCell key={col.key} align={col.align || "left"}>
                  {col.tooltip ? (
                    <Tooltip title={col.tooltip} arrow placement="top">
                      <span style={{ cursor: "help" }}>{col.label}</span>
                    </Tooltip>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              Array.from({ length: rowsPerPage }).map((_, idx) => (
                <TableRow key={`skeleton-${idx}`}>
                  {USER_COLUMNS.map((col) => {
                    if (col.key === "status") {
                      return (
                        <TableCell key={col.key} align="center">
                          <Skeleton
                            variant="rounded"
                            sx={{ mx: "auto" }}
                            width={80}
                            height={24}
                            animation="wave"
                          />
                        </TableCell>
                      );
                    }
                    if (col.key === "actions") {
                      return (
                        <TableCell key={col.key} align="center">
                          <Skeleton
                            variant="rounded"
                            sx={{ mx: "auto" }}
                            width={120}
                            height={28}
                            animation="wave"
                          />
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={col.key}>
                        <Skeleton
                          variant="text"
                          height={20}
                          animation="wave"
                          width={col.key === "email" ? "80%" : "60%"}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={USER_COLUMNS.length} align="center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user, index) => {
                const statusUpper = (user.status || "").toUpperCase();
                const isInactive =
                  statusUpper === "INACTIVE" || statusUpper === "DEACTIVATED";
                const config = statusConfig(user.status || "");

                return (
                  <TableRow
                    key={`${user.email}-${index}`}
                    sx={{
                      transition: "all 0.15s ease-in-out",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                      "&:nth-of-type(even)": {
                        bgcolor: alpha(theme.palette.action.hover, 0.02),
                      },
                      "&:nth-of-type(even):hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: theme.palette.primary.main,
                          }}
                        >
                          {user.firstName?.[0]}
                          {user.lastName?.[0]}
                        </Avatar>
                        {renderWithTooltip(user.firstName)}
                      </Box>
                    </TableCell>
                    <TableCell>{renderWithTooltip(user.lastName)}</TableCell>
                    <TableCell>{renderWithTooltip(user.rank)}</TableCell>
                    <TableCell>{renderWithTooltip(user.badgeNumber)}</TableCell>
                    <TableCell>{renderWithTooltip(user.department)}</TableCell>
                    <TableCell>{renderWithTooltip(user.email)}</TableCell>
                    <TableCell>
                      {renderWithTooltip(getStationRoleLabel(user.role))}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          color: config.color,
                          backgroundColor: config.backgroundColor,
                          border: `1px solid ${config.borderColor}`,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          minWidth: 100,
                          justifyContent: "flex-start",
                        }}
                      >
                        {config.icon}
                        {isInactive ? "INACTIVE" : user.status}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ overflow: "visible", textOverflow: "clip" }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, user)}
                          aria-label={`Actions for ${
                            [user.firstName, user.lastName]
                              .filter(Boolean)
                              .join(" ") || user.email
                          }`}
                          aria-controls={
                            activeMenuUser?.email === user.email && menuAnchorEl
                              ? "users-table-actions-menu"
                              : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={
                            activeMenuUser?.email === user.email && Boolean(menuAnchorEl)
                              ? true
                              : undefined
                          }
                          sx={{
                            color: "text.secondary",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: "primary.main",
                            },
                          }}
                        >
                          <MoreHorizIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredUsers.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.400",
            bgcolor: "background.paper",
            width: "100%",
          }}
        >
          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            totalItems={filteredUsers.length}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </Paper>
      )}

      <Menu
        id="users-table-actions-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiMenuItem-root": {
                px: 2,
                py: 1.5,
                fontSize: "0.875rem",
                "& .MuiListItemIcon-root": {
                  minWidth: 32,
                },
              },
            },
          },
        }}
      >
        {activeMenuUser && (() => {
          const userStatus = (activeMenuUser.status || "").toUpperCase();
          const inactive = userStatus === "INACTIVE" || userStatus === "DEACTIVATED";
          const pending = userStatus === "PENDING" || userStatus === "INVITED";
          // Invitation-only rows have no account yet (id null) — account
          // actions (disable/reactivate/reset password) can only 404.
          const invitedOnly = !activeMenuUser.id;
          const updating = statusUpdatingEmail === activeMenuUser.email;
          const resending = resendingEmail === activeMenuUser.email;
          const resettingPassword = resettingPasswordEmail === activeMenuUser.email;
          // Mirrors the backend's rank rules — hidden where a request could
          // only 403 (own row, or a member at/above the caller's rank).
          const canEditRole =
            !invitedOnly &&
            canChangeStationRole(callerRoleSlug, activeMenuUser, currentUserEmail);

          return [
            (pending || inactive) && (
              <MenuItem
                key="resend"
                onClick={() => {
                  onResendInvite(activeMenuUser);
                  handleMenuClose();
                }}
                disabled={resending}
              >
                <ListItemIcon>
                  <MailOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{resending ? "Sending..." : "Resend email invite"}</ListItemText>
              </MenuItem>
            ),
            !invitedOnly &&
              (inactive ? (
                <MenuItem
                  key="activate"
                  onClick={() => {
                    onStatusChange(activeMenuUser, "activate");
                    handleMenuClose();
                  }}
                  disabled={updating}
                >
                  <ListItemIcon>
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: "success.main" }}>Reactivate account</ListItemText>
                </MenuItem>
              ) : (
                <MenuItem
                  key="deactivate"
                  onClick={() => {
                    onStatusChange(activeMenuUser, "deactivate");
                    handleMenuClose();
                  }}
                  disabled={updating}
                >
                  <ListItemIcon>
                    <BlockIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText sx={{ color: "text.secondary" }}>Disable account</ListItemText>
                </MenuItem>
              )),
            !invitedOnly && (
              <MenuItem
                key="edit-badge"
                onClick={() => {
                  onEditBadge(activeMenuUser);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <BadgeOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit badge number</ListItemText>
              </MenuItem>
            ),
            canEditRole && (
              <MenuItem
                key="change-role"
                onClick={() => {
                  onEditRole(activeMenuUser);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <ManageAccountsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change role</ListItemText>
              </MenuItem>
            ),
            !invitedOnly && (
              <MenuItem
                key="reset-password"
                onClick={() => {
                  onResetPassword(activeMenuUser);
                  handleMenuClose();
                }}
                disabled={resettingPassword}
              >
                <ListItemIcon>
                  <RestartAltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {resettingPassword ? "Sending..." : "Reset password"}
                </ListItemText>
              </MenuItem>
            ),
          ].filter(Boolean);
        })()}
      </Menu>
    </Paper>
  );
};

export default UsersTable;
